/**
 * BHADKO OTT - Live HLS Video Player Engine
 * Supports HLS.js streaming, native Safari HLS, retry backoffs, 
 * adaptive quality levels, viewer stats, and TV broadcast overlays.
 */

import Hls from 'hls.js';

export class PlayerEngine {
  constructor(videoElement, containerElement, options = {}) {
    this.video = videoElement;
    this.container = containerElement;
    this.options = options;
    this.hls = null;
    this.currentChannel = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.retryTimer = null;
    this.isMuted = true;
    this.onStateChange = options.onStateChange || (() => {});
    this.clockInterval = null;

    this.initListeners();
    this.startClock();
  }

  initListeners() {
    this.video.addEventListener('playing', () => {
      this.retryCount = 0;
      this.setState('playing');
    });

    this.video.addEventListener('waiting', () => {
      this.setState('buffering');
    });

    this.video.addEventListener('error', (e) => {
      console.warn("Video element error", e);
      this.handlePlaybackFailure();
    });

    // Handle user interaction for autoplay audio policy
    this.video.addEventListener('volumechange', () => {
      this.isMuted = this.video.muted;
      this.updateVolumeUI();
    });
  }

  setState(state, errorMsg = '') {
    this.state = state; // 'loading' | 'playing' | 'buffering' | 'error' | 'offline'
    this.onStateChange(state, errorMsg);
  }

  loadChannel(channel) {
    if (!channel) return;
    
    // Stop any previous instance and release media session
    this.releaseSession();
    this.currentChannel = channel;
    this.retryCount = 0;

    // Check if channel is declared offline
    if (channel.status === 'OFFLINE') {
      this.setState('offline', 'ચેનલ હાલમાં ઓફલાઇન છે / Broadcast currently off-air');
      return;
    }

    this.setState('loading');
    this.connectStream(channel.streamUrl);
  }

  connectStream(streamUrl) {
    if (!streamUrl) {
      this.setState('error', 'કોઈ સ્ટ્રીમ URL ઉપલબ્ધ નથી');
      return;
    }

    // Modern browsers with Hls.js support
    if (Hls.isSupported()) {
      if (this.hls) {
        this.hls.destroy();
      }

      this.hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 60,
        manifestLoadingTimeOut: 8000,
        manifestLoadingMaxRetry: 2,
        levelLoadingTimeOut: 8000
      });

      this.hls.loadSource(streamUrl);
      this.hls.attachMedia(this.video);

      this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        // Report available qualities
        if (this.options.onQualityLevels && data.levels) {
          this.options.onQualityLevels(data.levels);
        }
        
        // Browser autoplay policy: initial play is muted
        this.video.muted = this.isMuted;
        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log("Autoplay prevented or paused, waiting for user gesture", error);
            this.setState('ready_to_play');
          });
        }
      });

      this.hls.on(Hls.Events.ERROR, (event, data) => {
        console.warn("HLS Event Error:", data.type, data.details);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log("Network error encountered, attempting recovery...");
              this.handleNetworkFailure();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log("Media error encountered, trying to recover media...");
              this.hls.recoverMediaError();
              break;
            default:
              console.error("Fatal unrecoverable HLS error");
              this.handlePlaybackFailure();
              break;
          }
        }
      });

    } else if (this.video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native Apple HLS (Safari / iOS)
      this.video.src = streamUrl;
      this.video.muted = this.isMuted;
      this.video.play().catch(e => {
        console.log("Native Safari play prevented", e);
        this.setState('ready_to_play');
      });
    } else {
      this.setState('error', 'તમારા બ્રાઉઝરમાં HLS સ્ટ્રીમિંગ સપોર્ટેડ નથી.');
    }
  }

  handleNetworkFailure() {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      const delay = this.retryCount * 2500;
      this.setState('buffering', `કનેક્શન ફરી સ્થાપિત થઈ રહ્યું છે (પ્રયાસ ${this.retryCount}/${this.maxRetries})...`);
      
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => {
        if (this.currentChannel?.backupStreamUrl && this.retryCount > 1) {
          console.log("Switching to backup stream:", this.currentChannel.backupStreamUrl);
          this.connectStream(this.currentChannel.backupStreamUrl);
        } else if (this.hls) {
          this.hls.startLoad();
        }
      }, delay);
    } else {
      this.handlePlaybackFailure();
    }
  }

  handlePlaybackFailure() {
    clearTimeout(this.retryTimer);
    this.setState('error', 'લાઈવ સ્ટ્રીમ કનેક્શન અસ્થાયી રૂપે અનુપલબ્ધ છે.');
  }

  retry() {
    this.retryCount = 0;
    if (this.currentChannel) {
      this.loadChannel(this.currentChannel);
    }
  }

  setQuality(levelIndex) {
    if (this.hls) {
      this.hls.currentLevel = levelIndex; // -1 for auto
    }
  }

  togglePlay() {
    if (this.video.paused) {
      this.video.play();
    } else {
      this.video.pause();
    }
  }

  toggleMute() {
    this.video.muted = !this.video.muted;
    this.isMuted = this.video.muted;
    this.updateVolumeUI();
  }

  setVolume(fraction) {
    this.video.volume = Math.max(0, Math.min(1, fraction));
    if (this.video.volume > 0 && this.video.muted) {
      this.video.muted = false;
      this.isMuted = false;
    }
    this.updateVolumeUI();
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      if (this.container.requestFullscreen) {
        this.container.requestFullscreen();
      } else if (this.container.webkitRequestFullscreen) {
        this.container.webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  updateVolumeUI() {
    if (this.options.onVolumeChange) {
      this.options.onVolumeChange(this.video.volume, this.video.muted);
    }
  }

  startClock() {
    // Updates live broadcast clock for realism
    this.clockInterval = setInterval(() => {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const clockEl = document.getElementById('broadcastLiveClock');
      if (clockEl) {
        clockEl.textContent = `${hours}:${minutes}`;
      }
    }, 1000);
  }

  releaseSession() {
    clearTimeout(this.retryTimer);
    if (this.hls) {
      this.hls.stopLoad();
      this.hls.detachMedia();
      this.hls.destroy();
      this.hls = null;
    }
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
  }

  destroy() {
    this.releaseSession();
    clearInterval(this.clockInterval);
  }
}
